import { gsap } from "gsap";
import { UiTypes } from "@various/constants";
import { disposeSubAxis } from "../utils";

type RelativeMouseBodyEV = MouseEvent | { pageX: number; pageY: number };
type RelativeMouseBodyFixedEV = MouseEvent | { clientX: number; clientY: number };
type RelativeMouseBodyOption = {
    //* 偏移距离
    offsetX: number;
    offsetY: number;
    //* 定位位置
    align?: { x?: UiTypes.align; y?: UiTypes.align };
    /**
     * * 边界位置，{ x：X轴边界, y: Y轴边界, strategy: 处理策略 }
     * * strategy: Default, 超出边界时若对轴空间充足则反方向定位
     * * strategy: Perfect, 尽可能展示完整, 但会导致小三角位置错位
     * * strategy: Disable, 禁用边界处理函数
     */
    boundary?: { x?: { min?: number; max?: number }; y?: { min?: number; max?: number }; strategy?: "Perfect" | "Disable" | "Default" };
    //* 是否初始化位置
    initialization?: boolean;
};

/**
 * @name 窗口相对于鼠标定位的可视区域边界算法（设置）（适用于Absolute定位）
 * @param ev Mouse event对象
 * @param view 窗口
 * @param option
 * @returns
 */
export const relativeMouseBody = (ev: RelativeMouseBodyEV, view: HTMLElement, option: RelativeMouseBodyOption) => {
    //* 坐标位置初始化
    const data = relativeMouseBodySite(ev, view, option);
    //* 未进行过定位时, 默认定位到当前位置
    if (option.initialization) {
        gsap.set(view, { x: data.offsetX, y: data.offsetY });
    }

    //* 设置属性
    gsap.to(view, { duration: 0.2, x: data.offsetX, y: data.offsetY });
};

/**
 * @name 窗口相对于鼠标定位的可视区域边界算法（设置）（适用于Fixed定位）
 * @param ev Mouse event对象
 * @param view 窗口
 * @param option
 * @returns
 */
export const relativeMouseBodyFixed = (ev: RelativeMouseBodyFixedEV, view: HTMLElement, option: RelativeMouseBodyOption) => {
    //* 坐标位置初始化
    const data = relativeMouseBodyFixedSite(ev, view, option);
    //* 未进行过定位时, 默认定位到当前位置
    if (option.initialization) {
        gsap.set(view, { x: data.offsetX, y: data.offsetY });
    }

    //* 设置属性
    gsap.to(view, { duration: 0.2, x: data.offsetX, y: data.offsetY });
};

/**
 * @name 窗口相对于鼠标定位的可视区域边界算法（坐标）（适用于Absolute定位）
 * @param ev Mouse event对象
 * @param view 窗口
 * @param option
 * @returns
 */
export const relativeMouseBodySite = (ev: RelativeMouseBodyEV, view: HTMLElement, option: RelativeMouseBodyOption) => {
    //* X轴的位置计算
    const offsetXMin = option.boundary?.x?.min || 0;
    const offsetXMax = option.boundary?.x?.max || innerWidth;
    const offsetX = disposeSubAxis(
        { container: 0, space: option.offsetX, view: view.clientWidth },
        {
            min: offsetXMin + scrollX,
            max: offsetXMax + scrollX,
            align: option.align?.x || "start",
            offset: ev.pageX,
            strategy: option.boundary?.strategy,
        }
    );

    //* Y轴的位置计算
    const offsetYMin = option.boundary?.y?.min || 0;
    const offsetYMax = option.boundary?.y?.max || innerHeight;
    const offsetY = disposeSubAxis(
        { container: 0, space: option.offsetY, view: view.clientHeight },
        {
            min: offsetYMin + scrollY,
            max: offsetYMax + scrollY,
            align: option.align?.y || "start",
            offset: ev.pageY,
            strategy: option.boundary?.strategy,
        }
    );

    return { offsetY: offsetY.offset, offsetX: offsetX.offset };
};

/**
 * @name 窗口相对于鼠标定位的可视区域边界算法（坐标）（适用于Fixed定位）
 * @param ev Mouse event对象
 * @param view 窗口
 * @param option
 * @returns
 */
export const relativeMouseBodyFixedSite = (ev: RelativeMouseBodyFixedEV, view: HTMLElement, option: RelativeMouseBodyOption) => {
    //* X轴的位置计算
    const offsetXMin = option.boundary?.x?.min || 0;
    const offsetXMax = option.boundary?.x?.max || innerWidth;
    const offsetX = disposeSubAxis(
        { container: 0, space: option.offsetX, view: view.clientWidth },
        { min: offsetXMin, max: offsetXMax, align: option.align?.x || "start", offset: ev.clientX, strategy: option.boundary?.strategy }
    );

    //* Y轴的位置计算
    const offsetYMin = option.boundary?.y?.min || 0;
    const offsetYMax = option.boundary?.y?.max || innerHeight;
    const offsetY = disposeSubAxis(
        { container: 0, space: option.offsetY, view: view.clientHeight },
        { min: offsetYMin, max: offsetYMax, align: option.align?.y || "start", offset: ev.clientY, strategy: option.boundary?.strategy }
    );

    return { offsetY: offsetY.offset, offsetX: offsetX.offset };
};
